import { useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import { ApiError } from 'aws-amplify/api';
import Layout from '../mui-components/Layout';
import { Button, Grid, Typography, Stack, Box, Alert } from '@mui/material';
import { useEffect, useState } from 'react';
import moment from 'moment';
import Readings from './readings';
import Divider from '@mui/material/Divider';
import { timeFormat } from '../data/formats';
import { pageTitles } from '../data/pageTitles';
import { useGlobalContext } from '../context/GlobalStore';
import AlertModal from '../mui-components/AlertModal';
import ErrorAlertModal from '../mui-components/ErrorAlertModal';
import ChangeApiKeyModal from '../mui-components/ApiKeys/ChangeApiKeyModal';
import LoadingDialog from '../mui-components/LoadingDialog';
import Snackbar from '@mui/material/Snackbar';
import apiClient from '../utils/apiClient';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';



export default function Device() {
  document.title = pageTitles.device;
  const { state, setDevices, isDeviceLoading } = useGlobalContext(); // Added isDeviceLoading
  let location = useLocation();
  const { id } = useParams();
  const [deviceData, setDeviceData] = useState([]);
  const [allowAdmin, setAllowAdmin] = useState(true);
  const [deactivateMessage, setDeactivateMessage] = useState("");
  const [deviceBillingStatus, setDeviceBillingStatus] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [openChangeApiKey, setOpenChangeApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState({
      api_key_id: '',
    });
  const [currentApiKey, setCurrentApiKey] = useState("");
  const [originalApiKey, setOriginalApiKey] = useState("");
  const [originalDevice, setOriginalDevice] = useState(null); // Changed to null
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true); // Added pageLoading state
  
  
  // Reminder state variables
  const [showReminders, setShowReminders] = useState(false);
  const [remindersActive, setRemindersActive] = useState(false);
  const [reminderTimes, setReminderTimes] = useState([null, null, null, null, null]);
  const [originalReminderTimes, setOriginalReminderTimes] = useState([null, null, null, null, null]);
  const [reminderTimesChanged, setReminderTimesChanged] = useState(false);

  const InfoItem = ({ label, value, sx }) => (
    <Box sx={{ display: 'flex', mb: 0.5, ...sx }}>
      <Typography variant="body1" sx={{ color: 'text.secondary', width: 120, minWidth: 120, flexShrink: 0 }} mr={1}>
        {label}:
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
        {(value !== null && value !== undefined && value !== '') ? value : 'N/A'}
      </Typography>
    </Box>
  );

  // Snack bar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");


  useEffect(() => {
    if (isDeviceLoading) {
      setPageLoading(true);
      return; // Wait until global device data is loaded
    }

    // Global loading is done, so we can stop showing the page loading indicator.
    setPageLoading(false);

    const device = state.devices?.find((d) => d.device_imei === id);

    if (device && state.apiKey) {
      // If we found a device, set all the component's state from it.
      setOriginalDevice(device);
      setCurrentApiKey(state.apiKey.name);
      setOriginalApiKey(state.apiKey.name);
      setDeviceBillingStatus(device.billing_status || '');

      const supportsReminders = ['900-LTE'].includes(device.device_model);
      setShowReminders(supportsReminders);

      if (supportsReminders) {
        const remindersActiveState = device.reminders_active;
        let reminderTimesArray = convertRawMinutesToTime(device.reminder_times);
        while (reminderTimesArray.length < 5) {
          reminderTimesArray.push(null);
        }
        setRemindersActive(remindersActiveState);
        setReminderTimes(reminderTimesArray);
        setOriginalReminderTimes([...reminderTimesArray]);
      } else {
        // Explicitly reset reminder states if the device doesn't support them.
        setShowReminders(false);
        setRemindersActive(false);
        setReminderTimes([null, null, null, null, null]);
        setOriginalReminderTimes([null, null, null, null, null]);
      }

      // This legacy data structure is still used by some parts of the component.
      const data = [
        { label: 'Device Id', val: device.device_id },
        { label: 'Device Imei', val: device.device_imei },
        { label: 'Device Sim', val: device.device_sim },
        { label: 'Device Type', val: device.device_type },
        { label: 'Device Model', val: device.device_model },
        { label: 'Patient Id', val: device.patient_id },
        { label: 'Practice Id', val: device.practice_id },
        { label: 'API Key', val: state.apiKey.name },
        { label: 'Billing Status', val: device.billing_status },
        { label: 'Renewal Date', val: device.renewal_date ? moment(device.renewal_date).format('L, LT') : 'N/A' },
        { label: 'First Reading', val: device.first_measurement_timestamp ? moment(device.first_measurement_timestamp).format('L, LT') : 'N/A' },
        { label: 'Last Reading', val: device.last_measurement_timestamp ? moment(device.last_measurement_timestamp).format('L, LT') : 'N/A' },
      ];
      setDeviceData(data);
    } else {
      // If no device is found, ensure we render nothing by setting originalDevice to null.
      setOriginalDevice(null);
    }
  }, [isDeviceLoading, state.devices, state.apiKey, id]);



  useEffect(() => {
    setDeviceData((prev) => {
      return prev.map((d) => {
        if (d.label === 'API Key') {
          return { ...d, val: currentApiKey };
        }
        return d;
      })
    })
  }, [currentApiKey])

  useEffect(() => {
    
    if (!state?.policies?.hasOwnProperty("Admin")) {
      setAllowAdmin(false);
      setDeactivateMessage("Only administrators can deactivate devices.")
    } else {
      const policies = Object.values(state.policies).reduce((result, inner) => {
        const apiKeyObj = Object.entries(inner).find(([key]) => key === state.apiKey.key);
        return apiKeyObj ? apiKeyObj[1] : result;
      }, [])
      if (!policies.includes("pylo:DeactivateDevices")) {
        setAllowAdmin(false);
        setDeactivateMessage("Only administrators can deactivate devices.")
      } else {
        setAllowAdmin(true);
        setDeactivateMessage("") 
      }
    }
  }, [state.account])

  async function deactivateReactivateDevice(action) {
    setIsLoading(true);
    const device_customer_id = state?.account?.pylo_accounts?.find(
      (a) => a.name === currentApiKey
    )['id'];
    const imeis = [id];
    try {

      const response = await apiClient.post(
        '/devices', 
        {
          action: action === 'deactivate' ? 'deactivate_devices' : 'reactivate_devices',
          account_id: device_customer_id,
          device_imeis: imeis
        }, 
        {
          retryOptions: { maxRetries: 0 }
        },
        true
      );

      if (response) {
        if (Object.keys(response["failures"]).length > 0) {
          // Device was not able to have its billing status changed
          setAlertMessage(`Device could not be ${action === 'deactivate' ? 'deactivated' : 'reactivated'}. \n\n${imeis[0]}: ${response["failures"][imeis[0]]}`)
          setAlertTitle("Error");
          setOpenErrorAlert(true);
          setIsLoading(false);
          return;
        }
        
        let device_data = deviceData;
        for (let field of device_data) {
          if (field.label === 'Billing Status') {
            field.val = action === 'deactivate' ? 'pending_deactivation' : 'active';
          }
        }
        setDeviceData(device_data);
        setDeviceBillingStatus(action === 'deactivate' ? 'pending_deactivation' : 'active');
        let devices_copy = state.devices;
        for (let device of devices_copy) {
          if (device.device_imei === id) {
            device.billing_status = action === 'deactivate' ? 'pending_deactivation' : 'active';
            break;
          }
        }
        setDevices(devices_copy);
        setSnackbarMessage(`Device ${action === 'deactivate' ? 'deactivated' : 'reactivated'} successfully`);
        setOpenSnackbar(true);
        setIsLoading(false);
      }
    } catch (error) {
      let errorMessage = '';
      if (error instanceof ApiError) {
        if (error.response) {
          const { statusCode, headers, body} = error.response;
          console.log(`Received ${statusCode} error response with payload ${body}`);
          errorMessage = JSON.parse(body);
        }
      } else {
        // standard error
        errorMessage = error.message;
      }
      setAlertMessage(`Error deactivating devices: ${error.message}`);
      setAlertTitle("Error");
      setOpenErrorAlert(true);
      setIsLoading(false);
    }
  };
  
  async function moveDeviceApiKey() {
    setIsLoading(true);
    
    const currentKey = state?.account?.pylo_accounts?.find(
      (a) => a.name === currentApiKey
    );
    const newKey = state.account.pylo_accounts.find(
      (a) => a.key_id === newApiKey.api_key_id
    );
    
    try {
      const response = await apiClient.post(
        '/devices',
        {
          action: "move_api_key",
          account_id: state.account.pylo_accounts[0]["user_group_id"],
          device_imei: id,
          customer_id: currentKey.id,
          new_customer_id: newKey.id,
        },
        {
          retryOptions: { maxRetries: 0 }
        },
        true
      );

      if (response) {
        setCurrentApiKey(newKey.name);
        setSnackbarMessage("Device API key moved successfully");
        setOpenSnackbar(true);
        let devices_copy = [];
        if (newKey.name !== originalApiKey) {
          devices_copy = state.devices.filter((d) => d.device_imei !== id);
        } else {
          devices_copy = state.devices;
          devices_copy.push(originalDevice);
        }
        setDevices(devices_copy);
        setIsLoading(false);
      }
    } catch (error) {
      let errorMessage = '';
      if (error instanceof ApiError) {
        if (error.response) {
          const { statusCode, headers, body} = error.response;
          const parsedBody = JSON.parse(body);
          let message = parsedBody;
          if (parsedBody.message) {
            message = parsedBody.message;
          }
          console.log(`Received ${statusCode} error response with payload ${parsedBody}`);
          errorMessage = message;
        }
      } else {
        errorMessage = error.message;
      }
      setAlertMessage(`Error moving device API key: ${errorMessage}`);
      setAlertTitle("Error");
      setOpenErrorAlert(true);
      setIsLoading(false);
    }
  }

  async function toggleReminders() {
    setIsLoading(true);
    let action = remindersActive ? "disable_reminders" : "enable_reminders";
    const device_customer_id = state?.account?.pylo_accounts?.find(
      (a) => a.name === currentApiKey
    )['id'];
    try{
      const response = await apiClient.post(
        '/devices',
        {
          action: action,
          account_id: device_customer_id,
          device_imei: id,
        },
        {
          retryOptions: { maxRetries: 0 }
        },
        true
      );  
      if (response) {
        const newRemindersActiveState = !remindersActive;

        let finalAlertMessage = "";
        let finalAlertTitle = "";
        let uiTimesToSet = [...reminderTimes];
        let timesChangedToSet = reminderTimesChanged;

        const deviceUpdatePayload = { reminders_active: newRemindersActiveState };

        if (newRemindersActiveState) {
          const uiTimesAreCurrentlyAllNull = reminderTimes.every(time => time === null);
          const originalTimesHadValues = originalReminderTimes.some(time => time !== null);

          if (uiTimesAreCurrentlyAllNull && originalTimesHadValues) {
            uiTimesToSet = [...originalReminderTimes];
            const originalTimesInMinutes = originalReminderTimes.map(time => {
              return time ? time.hour() * 60 + time.minute() : null;
            });
            while (originalTimesInMinutes.length < 5) {
              originalTimesInMinutes.push(null);
            }
            deviceUpdatePayload.reminder_times = originalTimesInMinutes;

            timesChangedToSet = false;
            finalAlertMessage = `Reminders activated successfully. Original reminder times have been restored.`;
            finalAlertTitle = 'Reminders Activated';
          } else {
            finalAlertMessage = `Reminders activated successfully`;
            finalAlertTitle = 'Activate Reminders';
          }
        } else {
          finalAlertMessage = `Reminders deactivated successfully`;
          finalAlertTitle = 'Deactivate Reminders';
        }

        const currentGlobalDevices = state.devices;
        const updatedGlobalDevices = currentGlobalDevices.map(d => {
          if (d.device_imei === id) {
            return {
              ...d,
              ...deviceUpdatePayload
            };
          }
          return d;
        });
        setDevices(updatedGlobalDevices);

        setRemindersActive(newRemindersActiveState);
        setReminderTimes(uiTimesToSet);
        setReminderTimesChanged(timesChangedToSet);
        setAlertMessage(finalAlertMessage);
        setAlertTitle(finalAlertTitle);
        setOpenAlert(true);

      } else {
        setAlertMessage(`Error updating reminders`);
        setAlertTitle("Error");
        setOpenErrorAlert(true);
      }
    } catch (error) {
      let errorMessage = '';
      if (error instanceof ApiError) {
        if (error.response) {
          const { statusCode, headers, body} = error.response;
          console.log(`Received ${statusCode} error response with payload ${body}`);
          errorMessage = JSON.parse(body);
        }
      } else {
        // standard error
        errorMessage = error.message;
      }
      setAlertMessage(`Error updating reminders: ${errorMessage}`);
      setAlertTitle("Error");
      setOpenErrorAlert(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function updateReminderTimes() {
    setIsLoading(true);
    // convert reminder times to raw minutes
    const reminderTimesMinutes = reminderTimes.map((time) => {
      return time ? time.hour() * 60 + time.minute() : null;
    });
    const device_customer_id = state?.account?.pylo_accounts?.find(
      (a) => a.name === currentApiKey
    )['id'];
    try {
      const response = await apiClient.post(
        '/devices',
        {
          action: 'update_reminder_times',
          account_id: device_customer_id,
          device_imei: id,
          reminder_times: reminderTimesMinutes,
        },
        {
          retryOptions: { maxRetries: 0 }
        },
        true
      );
      if (response) {
        const currentGlobalDevices = state.devices;
        const updatedGlobalDevices = currentGlobalDevices.map(d => {
          if (d.device_imei === id) {
            return { ...d, reminder_times: reminderTimesMinutes };
          }
          return d;
        });
        setDevices(updatedGlobalDevices);

        setOriginalReminderTimes([...reminderTimes]);

        setAlertMessage('Reminder times updated successfully. Changes may take up to 24 hours to take effect.');
        setAlertTitle('Update Reminder Times');
        setOpenAlert(true);
      }
    } catch (error) {
      let errorMessage = '';
      if (error instanceof ApiError) {
        if (error.response) {
          const { statusCode, headers, body} = error.response;
          console.log(`Received ${statusCode} error response with payload ${body}`);
          errorMessage = JSON.parse(body);
        }
      } else {
        errorMessage = error.message;
      }
      setAlertMessage(`Error updating reminder times: ${errorMessage}`);
      setAlertTitle("Error");
      setOpenErrorAlert(true);
    } finally {
      setIsLoading(false);
    }

  }

  const convertRawMinutesToTime = (times) => {
    return times ? times.map(time => {
      if (typeof time === 'number') {
        const hours = Math.floor(time / 60);
        const minutes = time % 60;
        // Date here is arbitrary, just needs to be a valid date as we only care about the time
        return dayjs(`2025-05-01T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
      }
      return time || null;
    }) : 
    [null, null, null, null, null];
  };

  const handleDeactivateReactivate = () => {
    let action = deviceBillingStatus === "active" ? "deactivate" : "reactivate";
    deactivateReactivateDevice(action);
  };

  const handleMoveApiKey = (e) => {
    e.preventDefault();
    const newKey = state.account.pylo_accounts.find(
      (account) => account.key_id === newApiKey.api_key_id
    );
    if (newKey.name !== currentApiKey) {
      // User has requested an update
      moveDeviceApiKey();
    }
  };
  
  const handleToggleReminders = () => {
    toggleReminders();
  };

  const handleUpdateReminderTimes = async () => {
    const allTimesNull = reminderTimes.every(time => time === null);
    
    if (allTimesNull && remindersActive) {
      // If all times are null and reminders are active, deactivate reminders instead
      try {
        await toggleReminders();
        setAlertMessage('All reminder times were removed. Reminders have been deactivated.');
        setAlertTitle('Reminders Deactivated');
        setOpenAlert(true);
        setReminderTimesChanged(false);
      } catch (error) {
        console.error('Error deactivating reminders:', error);
        setAlertMessage('Failed to deactivate reminders. Please try again.');
        setAlertTitle('Error');
        setOpenErrorAlert(true);
      }
    } else if (!allTimesNull) {
      // Only update reminder times if there's at least one non-null time
      updateReminderTimes();
      setReminderTimesChanged(false);
    } else {
      // This case handles when all times are null but reminders are already inactive
      // Just reset the UI state without making API calls
      setReminderTimesChanged(false);
      setAlertMessage('No changes were made as reminders are already inactive.');
      setAlertTitle('No Changes');
      setOpenAlert(true);
    }
  };
  
  // Handle reminder time input changes
  const handleReminderTimeChange = (index, value) => {
    const newReminderTimes = [...reminderTimes];
    
    // If a time is set to null (cleared)
    if (value === null) {
      // Shift all times after this index to fill the gap
      for (let i = index; i < newReminderTimes.length - 1; i++) {
        newReminderTimes[i] = newReminderTimes[i + 1];
      }
      // Ensure the last position is null
      newReminderTimes[newReminderTimes.length - 1] = null;
    } else {
      // Just update the time at the specified index
      newReminderTimes[index] = value;
    }
    
    // Remove any null values between non-null values
    const nonNullTimes = newReminderTimes.filter(time => time !== null);
    const compactedTimes = [...nonNullTimes];
    
    // Fill the rest with null values to maintain 5 elements
    while (compactedTimes.length < 5) {
      compactedTimes.push(null);
    }
    
    setReminderTimes(compactedTimes);
    
    // Check if times have changed from original
    const changed = JSON.stringify(compactedTimes) !== JSON.stringify(originalReminderTimes);
    setReminderTimesChanged(changed);
  };
  
  return (
    <Layout title="Device Details">
      <Snackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <AlertModal
        open={openAlert}
        handleClose={() => setOpenAlert(false)}
        message={alertMessage}
        title={alertTitle}
      />
      <ErrorAlertModal
        open={openErrorAlert}
        handleClose={() => setOpenErrorAlert(false)}
        message={alertMessage}
        title={alertTitle}
      />
      <ChangeApiKeyModal
          open={openChangeApiKey}
          handleClose={() => setOpenChangeApiKey(false)}
          handleMoveApiKey={handleMoveApiKey}
          currentApiKey={currentApiKey}
          newApiKey={newApiKey}
          setNewApiKey={setNewApiKey}
          isLoading={isLoading}
        />
      <LoadingDialog open={isLoading || pageLoading} />

      {/* Conditional Rendering for page content */}
      {!pageLoading && originalDevice ? (
        <>
          <Stack direction="row" gap={2} display="flex" alignItems="center">
          {
            (deviceBillingStatus === "active" || 
              deviceBillingStatus === "pending_deactivation") && allowAdmin && (
              <Button
                variant="contained"
                onClick={handleDeactivateReactivate}
              >
                {
                  deviceBillingStatus === "active" ? "Deactivate" : "Reactivate"
                }
              </Button>
            )
          }
          {
            allowAdmin && originalDevice && (
              <Button
                variant="contained"
                onClick={() => setOpenChangeApiKey(true)}
              >
                Edit API Key Assignment
              </Button>
            )
          }
          {
            showReminders && allowAdmin && (
              <Button
                variant="contained"
                onClick={handleToggleReminders}
                color={remindersActive ? "secondary" : "primary"}
              >
                {remindersActive ? "Deactivate Reminders" : "Activate Reminders"}
              </Button>
            )
          }
          </Stack>
          <Box mt={1} mb={4}> {/* Retained original margins */}
            <Grid container spacing={3}>
              {/* Group 1: Device Details */}
              <Grid item xs={12} md={6} lg={4}>
                <Typography variant="h6" gutterBottom>Device Info</Typography>
                <InfoItem label="Device ID" value={originalDevice.device_id} />
                <InfoItem label="IMEI" value={originalDevice.device_imei} />
                <InfoItem label="SIM" value={originalDevice.device_sim} />
                <InfoItem label="Type" value={originalDevice.device_type} />
                <InfoItem label="Model" value={originalDevice.device_model} />
                <InfoItem label="API Key" value={currentApiKey} />
              </Grid>

              {/* Group 2: Operational & Configured Data */}
              <Grid item xs={12} md={6} lg={4}>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h6" gutterBottom>Configuration</Typography>
                    <InfoItem label="Patient ID" value={originalDevice.patient_id} />
                    <InfoItem label="Practice ID" value={originalDevice.practice_id} />
                    <InfoItem label="First Reading" value={originalDevice.first_measurement_timestamp ? moment(originalDevice.first_measurement_timestamp).format(timeFormat) : ''} />
                    <InfoItem label="Last Reading" value={originalDevice.last_measurement_timestamp ? moment(originalDevice.last_measurement_timestamp).format(timeFormat) : ''} />
                  </Box>
                </Stack>
              </Grid>

              {/* Group 3: Billing Information */}
              <Grid item xs={12} md={12} lg={4}> {/* md={12} for 2-col layout to take full width below first two */}
                <Typography variant="h6" gutterBottom>Billing Information</Typography>
                <InfoItem label="Billing Status" value={deviceBillingStatus} />
                <InfoItem label="Renewal Date" value={originalDevice.renewal_date ? moment(originalDevice.renewal_date).format(timeFormat) : ''} />
              </Grid>
            </Grid>
          </Box>
          
          {/* Reminder Settings Section - Full Width */}
          {showReminders && (
            <>
              <Divider sx={{ mt: 4, mb:3 }} /> {/* Added margin for spacing */}
              <Box mb={3}> {/* Retained original mb from when it was in the grid */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Reminder Settings</Typography>
                  {reminderTimesChanged && (
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleUpdateReminderTimes}
                      title={reminderTimes.every(time => time === null) ? 
                        "Removing all reminder times will deactivate reminders" : 
                        "Update reminder times"}
                    >
                      Update Reminder Times
                    </Button>
                  )}
                </Stack>
                
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Reminders Status: <span style={{ color: remindersActive ? 'green' : 'red' }}>{remindersActive ? 'Active' : 'Inactive'}</span>
                    </Typography>
                  </Grid>
                </Grid>
                
                {remindersActive && (
                  <>
                    {reminderTimesChanged && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Reminder time changes may take up to 24 hours to take effect.
                      </Alert>
                    )}
                    
                    <Typography variant="body1" mb={1}>
                      Configure up to 5 daily reminder times:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Note: Removing all reminder times will automatically deactivate reminders.
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {reminderTimes.map((time, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={2} key={`reminder-${index}`}>
                          <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                          >
                            <TimePicker
                              label={`Reminder ${index + 1}`}
                              value={time}
                              onChange={(e) => handleReminderTimeChange(index, e)}
                              disabled={!remindersActive}
                              clearable
                              slotProps={{
                                actionBar: {
                                  actions: ['clear']
                                },
                                field: {
                                  clearable: true
                                },
                                textField: {
                                  helperText: 'HH:MM'
                                },
                                digitalClock: {
                                  timeStep: 900 // 15 minutes in seconds
                                }
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </Box>
            </>
          )}
          
          <Divider sx={{ mt: showReminders ? 0 : 4, mb: 3 }} /> {/* Conditional margin for divider */} 
          <Readings
            token={state.token}
            id={id}
            accountId={location.state.account_id}
            currentApiKey={currentApiKey}
          />
        </>
      ) : null}
    </Layout>
  );
}
